<template>
  <div>
    <ReportMobileFrame
      v-if="$vuetify.breakpoint.smAndDown"
      :loading="isLoading"
      :empty="!isLoading && data.length === 0"
    >
      <template #filters>
        <div class="takings-mobile__top">
          <MobileDateField
            label="Start Date"
            :value="dates[0]"
            @input="onMobileDateChange(0, $event)"
          />
          <MobileDateField
            label="End Date"
            align="right"
            :value="dates[1] || dates[0]"
            @input="onMobileDateChange(1, $event)"
          />
          <button type="button" class="takings-mobile__total" @click="load">
            <span>Total</span>
            <strong>£{{ grandTotal.toFixed(2) }}</strong>
          </button>
        </div>
        <v-select
          v-if="stores.length > 1"
          :items="stores"
          item-text="name"
          item-value="id"
          v-model="selectedStoreId"
          label="Store"
          outlined
          dense
          hide-details
          class="mt-2"
          @change="load"
        />
      </template>

      <div v-for="group in groupedByMonth" :key="group.monthKey">
        <ReportGroupHeader :label="group.monthLabel">
          <template #stats>
            <div class="stat">
              <span class="stat__label">Cash</span>
              <span class="stat__value">£{{ group.totalCash.toFixed(2) }}</span>
            </div>
            <div class="stat">
              <span class="stat__label">Card</span>
              <span class="stat__value">£{{ group.totalCard.toFixed(2) }}</span>
            </div>
            <div class="stat">
              <span class="stat__label">Total</span>
              <span class="stat__value">£{{ group.totalAmount.toFixed(2) }}</span>
            </div>
          </template>
        </ReportGroupHeader>

        <ReportListRow
          v-for="day in group.days"
          :key="day.takingsDate"
          clickable
          @click="openDayOrders(day)"
        >
          <template #leading>
            <div class="date-circle">{{ moment(day.takingsDate).format('DD') }}</div>
          </template>
          <template #title>{{ moment(day.takingsDate).format('dddd') }}</template>
          <template #subtitle>{{ day.noOfSales }} sales &middot; {{ day.noOfItems }} items</template>
          <template #trailing>£{{ day.total.toFixed(2) }}</template>
        </ReportListRow>
      </div>

      <template #action>
        <v-btn block color="success" :loading="isLoading" x-large @click="exportReport">
          <v-icon left>mdi-file-export-outline</v-icon>
          EXPORT REPORT
        </v-btn>
      </template>
    </ReportMobileFrame>

    <template v-else>
    <v-toolbar flat class="toolbar-container mb-2" height='100'>
      <v-row >
        <v-col>
          <div class="toolbar-datepicker">
            <DateRangePicker :initialDates="dates" @blur="updateDates"  />
          </div>
        </v-col>

        <v-col v-if="stores.length > 1">
          <v-select :items="stores" item-text="name" v-model="selectedStoreId" item-value="id" />
        </v-col>
      </v-row>
    </v-toolbar>

     <v-row>
      <v-col>
        <v-btn block class="export-btn" type="button" color="primary" v-on:click="exportReport">
          EXPORT
        </v-btn>
      </v-col>

      <v-col>
        <v-btn block class="search-btn" type="button" color="success" v-on:click="load">
          SEARCH
        </v-btn>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <div>
          <TakingsChart :data="data"/>
        </div>
      </v-col>
    </v-row>

    <v-row>
       <v-data-table
        :headers="computedHeaders" :items="data" 
        :loading="isLoading"
        dense
        item-key="takingsDate" class="elevation-1 mt-5 flex-grow-1">

        <template v-slot:header="{ props: { headers } }">
          <thead>
            <tr>
              <th :colspan="headers.length" v-if="data != null && data.length > 0">
                Transactions: {{ data.map(d => d.noOfSales).reduce((x, y) => x + y) }} |
                Total: £{{ data.map(d => d.total).reduce((x, y) => x + y).toFixed(2) }} |
                Cash: £{{ data.map(d => d.cash).reduce((x, y) => x + y).toFixed(2) }} |
                Card: £{{ data.map(d => d.card).reduce((x, y) => x + y).toFixed(2) }} |
                Tips: £{{ data.map(d => d.tipTotal || 0).reduce((x, y) => x + y).toFixed(2) }} |
                Net Profit: £{{ data.map(d => d.netProfit).reduce((x, y) => x + y).toFixed(2) }}
              </th>
            </tr>
          </thead>
        </template>

        <template v-slot:[`item.takingsDate`]="{ item }">
          {{ moment(item.takingsDate).format("DD/MM/yyyy") }}
        </template>

        <template v-slot:[`item.total`]="{ item }">
          £{{ item.total.toFixed(2) }}
        </template>

        <template v-slot:[`item.totalDiscount`]="{ item }">
          £{{ item.totalDiscount.toFixed(2) }}
        </template>

         <template v-slot:[`item.cash`]="{ item }">
          £{{ item.cash.toFixed(2) }}
        </template>

         <template v-slot:[`item.card`]="{ item }">
          £{{ item.card.toFixed(2) }}
        </template>

        <template v-slot:[`item.vat`]="{ item }">
          £{{ item.vat.toFixed(2) }}
        </template>

        <template v-slot:[`item.netProfit`]="{ item }">
          £{{ item.netProfit.toFixed(2) }}
        </template>

        <template v-slot:[`item.totalExVAT`]="{ item }">
          £{{ item.totalExVAT.toFixed(2) }}
        </template>

        <template v-slot:[`item.totalBuyingPrice`]="{ item }">
          £{{ item.totalBuyingPrice.toFixed(2) }}
        </template>

        <template v-slot:[`item.serviceChargeCash`]="{ item }">
          £{{ item.serviceChargeCash.toFixed(2) }}
        </template>

        <template v-slot:[`item.serviceChargeCard`]="{ item }">
          £{{ item.serviceChargeCard.toFixed(2) }}
        </template>

        <template v-slot:[`item.serviceChargeTotal`]="{ item }">
          £{{ item.serviceChargeTotal.toFixed(2) }}
        </template>

        <template v-slot:[`item.tipCash`]="{ item }">
          £{{ (item.tipCash || 0).toFixed(2) }}
        </template>

        <template v-slot:[`item.tipCard`]="{ item }">
          £{{ (item.tipCard || 0).toFixed(2) }}
        </template>

        <template v-slot:[`item.tipTotal`]="{ item }">
          £{{ (item.tipTotal || 0).toFixed(2) }}
        </template>
      </v-data-table>
    </v-row>
    </template>

  </div>
</template>

<script>
import DateRangePicker from '@/components/DateRangePicker.vue';
import TakingsChart from '@/components/charts/TakingsChart.vue';
import ReportMobileFrame from '@/components/reports/ReportMobileFrame.vue';
import ReportGroupHeader from '@/components/reports/ReportGroupHeader.vue';
import ReportListRow from '@/components/reports/ReportListRow.vue';
import MobileDateField from '@/components/reports/MobileDateField.vue';
import { reportService } from '@/services';
import moment from 'moment';
import { saveAs } from 'file-saver';
import { b64toBlob } from '@/helpers';
import { userService } from '@/services';
import router from "../../router";

export default {

  name: 'Takings',
  components: {
    DateRangePicker,
    TakingsChart,
    ReportMobileFrame,
    ReportGroupHeader,
    ReportListRow,
    MobileDateField
  },
  data: vm => ({
    // dates: [new Date().toISOString().substr(0, 10)],
    stores: [],
    selectedStoreId: null,

    dates: [moment().add(-7, 'd').toISOString().substr(0, 10), moment().toISOString().substr(0, 10)],
    isLoading: false,

    data: [],
    headers: [
      { text: 'Date', align: 'start', sortable: true, filterable: true, value: 'takingsDate' },
      { text: '# Sales', align: 'start', sortable: true, filterable: true, value: 'noOfSales' },
      { text: '# Items', align: 'start', sortable: true, filterable: true, value: 'noOfItems' },
      { text: 'Cash', align: 'start', sortable: true, filterable: true, value: 'cash' },
      { text: 'Card', align: 'start', sortable: true, filterable: true, value: 'card' },
      { text: 'Discount', align: 'start', sortable: true, filterable: true, value: 'totalDiscount' },
      { text: 'Total (Ex VAT)', align: 'start', sortable: true, filterable: true, value: 'totalExVAT' },
      // { text: 'Cost of Goods (Ex VAT)', align: 'start', sortable: true, filterable: true, value: 'totalBuyingPrice' },
      { text: 'VAT', align: 'start', sortable: true, filterable: true, value: 'vat' },
      { text: 'Net Profit', align: 'start', sortable: true, filterable: true, value: 'netProfit' },
      { text: 'Total', align: 'start', sortable: true, filterable: true, value: 'total' },
      { text: 'Service Charge Cash', align: 'start', sortable: true, filterable: false, reportGroup: 'service', value: 'serviceChargeCash' },
      { text: 'Service Charge Card', align: 'start', sortable: true, filterable: false, reportGroup: 'service', value: 'serviceChargeCard' },
      { text: 'Service Charge Total', align: 'start', sortable: true, filterable: false, reportGroup: 'service', value: 'serviceChargeTotal' },
      { text: 'Tip Cash', align: 'start', sortable: true, filterable: false, reportGroup: 'tips', value: 'tipCash' },
      { text: 'Tip Card', align: 'start', sortable: true, filterable: false, reportGroup: 'tips', value: 'tipCard' },
      { text: 'Tip Total', align: 'start', sortable: true, filterable: false, reportGroup: 'tips', value: 'tipTotal' },
    ],
  }),
  async created () {
    if (userService.isStaff()) {
      router.push('/products');
    }
    // Load our stores
    this.stores = await reportService.getStores();
    this.stores.unshift({
      id: null,
      name: 'All'
    });

    this.load();
  },
  methods: {
    updateDates(newDates) {
      this.dates = newDates;
    },
    onMobileDateChange(index, value) {
      if (!value) return;
      const next = this.dates.slice();
      while (next.length <= index) next.push(value);
      next[index] = value;
      if (index === 0) next[1] = value;
      this.dates = next;
      this.load();
    },
    openDayOrders(day) {
      const date = moment(day.takingsDate).format('YYYY-MM-DD');
      this.$router.push({
        path: '/reports/orders',
        query: { startDate: date, endDate: date }
      });
    },
    async load() {
      var startDate = this.dates[0];
      var endDate = this.dates[0];
      if (this.dates.length > 1)
        endDate = this.dates[1];

      this.isLoading = true;
      this.data = await reportService.getTakingsReport(startDate, endDate, this.selectedStoreId);
      console.log(this.data);

      this.isLoading = false;
    },
    async exportReport() {
      var startDate = this.dates[0];
      var endDate = this.dates[0];
      if (this.dates.length > 1)
        endDate = this.dates[1];

      this.isLoading = true;
      var base64 = await reportService.exportTakingsReport(startDate, endDate, this.selectedStoreId);
      if (base64 == null) {
        this.isLoading = false;
        alert("Error exporting");
        return;
      }

      const blob = b64toBlob(base64, 'application/vnd.ms-excel');
      saveAs(blob, 'Takings Report.xlsx');

      this.isLoading = false;
    }
  },
  computed: {
    computedHeaders() {
      const hasServiceCharges = this.data.filter(d => d.serviceChargeTotal > 0).length > 0;
      const hasTips = this.data.filter(d => (d.tipTotal || 0) > 0).length > 0;

      return this.headers.filter(h => {
        if (h.reportGroup === 'service') return hasServiceCharges;
        if (h.reportGroup === 'tips') return hasTips;
        return true;
      });
    },
    grandTotal() {
      return this.data.reduce((sum, d) => sum + (d.total || 0), 0);
    },
    groupedByMonth() {
      const sorted = this.data.slice().sort((a, b) => {
        return new Date(b.takingsDate) - new Date(a.takingsDate);
      });

      const groups = new Map();
      for (const day of sorted) {
        const key = moment(day.takingsDate).format('YYYY-MM');
        if (!groups.has(key)) {
          groups.set(key, {
            monthKey: key,
            monthLabel: moment(day.takingsDate).format('MMMM YYYY'),
            days: [],
            totalCash: 0,
            totalCard: 0,
            totalAmount: 0
          });
        }
        const group = groups.get(key);
        group.days.push(day);
        group.totalCash += day.cash || 0;
        group.totalCard += day.card || 0;
        group.totalAmount += day.total || 0;
      }

      return Array.from(groups.values());
    }
  }
}
</script>

<style lang="scss" scoped>
  .toolbar-container {
    background: #FFF !important;
    padding: 10px;
    height: auto;
    border-radius: 8px;

    border: 1px solid lighten(#7B5CD8, 30);
  }

  .toolbar-content {
    display: flex;
    flex-direction: row;
    width: 100%;

    .toolbar-datepicker {
      width: 250px;
    }
  }

  .sub-total {
    ul {
      width: 100%;
      list-style-type: none;
      margin: 0;
      padding: 10px 10px;
    }
  }

  .export-btn {
    margin-right: 10px;
  }

  .takings-mobile__top {
    align-items: stretch;
    display: grid;
    gap: 8px;
    grid-template-columns: 1fr 1fr 1fr;
  }

  .date-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
  }

  .date-field span {
    color: var(--app-text);
    font-size: 11px;
    font-weight: 700;
    text-align: center;
  }

  .date-field input {
    background: var(--app-surface);
    border: 1px solid var(--app-border-strong);
    border-radius: var(--app-radius);
    color: var(--app-text);
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    min-width: 0;
    padding: 7px 6px;
    text-align: center;
    width: 100%;
  }

  .date-field input:focus {
    border-color: var(--app-primary);
    outline: none;
  }

  .takings-mobile__total {
    align-items: center;
    align-self: end;
    background: var(--app-primary);
    border: none;
    border-radius: var(--app-radius);
    box-shadow: 0 10px 22px rgba(15, 118, 110, 0.22);
    color: #fff;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    font-family: inherit;
    gap: 2px;
    height: 100%;
    justify-content: center;
    min-width: 0;
    padding: 4px 8px;
  }

  .takings-mobile__total span {
    font-size: 11px;
    font-weight: 600;
    opacity: 0.9;
  }

  .takings-mobile__total strong {
    font-size: 15px;
    font-weight: 800;
    line-height: 1.1;
    white-space: nowrap;
  }

  .date-circle {
    align-items: center;
    background: #e5e7eb;
    border-radius: 50%;
    color: var(--app-text);
    display: flex;
    font-size: 14px;
    font-weight: 700;
    height: 38px;
    justify-content: center;
    width: 38px;
  }
</style>
